import { useDispatch, useSelector } from 'react-redux'
import { HighlightBox, Button as SonansButton } from '@sonans/components'
import { Container, Text, Message, LoadingSpinner } from '@/components/UI'
import { AppDispatch, AppState } from '@/types'
import { CourseSearch } from '@/components/Courses/CourseSearch'
import { CourseList } from '@/components/Courses/CourseList'
import { ResultsCardScroll } from '@/components/Results/ResultsCardScroll'
import { AppLayout } from '@/components/UI/AppLayout'
import { useNavigate, useParams } from 'react-router-dom'
import React, { useEffect } from 'react'
import axios from 'axios'
import { API } from '@/utils'
import {
    resetGrades,
    setGrades,
    setTwentyThreeFiveRule,
} from '@/reducers/grades'
import { setBirthYear, setExperiences } from '@/reducers/experiences'
import {
    setDefaultCourses,
    setAdditionalCourses,
    setIsLoading,
    setError,
} from '@/reducers/courses'
import { setEducationalPrograms } from '@/reducers/educationalPrograms'
import { setReferenceCode, setUserAlias } from '@/reducers/referenceCode'
import { setFavoriteObjects } from '@/reducers/favoriteStudyFields'
import SaveResults from '@/components/UI/SaveResults'
import Experiences from '@/components/Experiences/Experiences'
import { Results } from '@/pages/Results'
import { helperTextFillOutGrades } from '@/helpers/HelperTexts'
import { setBetterThenRegular } from '@/reducers/results'
import { BookCounseling } from '@/components/UI/BookCounseling'
import { scanTranscriptImage } from '@/api/aiScanner'
import { mapScanToCourseGrades } from '@/helpers/aiScannerMapping'
import { addCourse } from '@/reducers/courses'
import { getLegacyCourses } from '@/api/courses'

export function Courses() {
    const youngster = useSelector(
        (state: AppState) => state.experiences.youngster
    )
    const betterThenRegular = useSelector(
        (state: AppState) => state.results.betterThenRegular
    )
    const gradePoints = useSelector(
        (state: AppState) => state.results.gradePoints
    )

    const defaultCourses = useSelector(
        (state: AppState) => state.courses.defaultCourses
    )
    const isLoading = useSelector((state: AppState) => state.courses.isLoading)
    const error = useSelector((state: AppState) => state.courses.error)

    const additionalCourses = useSelector(
    (state: AppState) => state.courses.additionalCourses
    )

    const [isScanning, setIsScanning] = React.useState(false)
    const [scanError, setScanError] = React.useState<string>('')
    const [scanVersion, setScanVersion] = React.useState(0)
    const [scanInfo, setScanInfo] = React.useState<{
        usedPipeline?: string | null
        matchedCount?: number
        unknownCodes?: { kode: string; fag?: string | null }[]
    } | null>(null)

    const { referenceCode } = useParams()
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()

    useEffect(() => {
        dispatch(setBetterThenRegular())
    }, [gradePoints])

    useEffect(() => {
        if (referenceCode) {
            dispatch(setReferenceCode(referenceCode))
            const fetchData = async () => {
                try {
                    dispatch(setError(''))
                    dispatch(setIsLoading(true))
                    const response = await axios.get(
                        `${API('calculations')}/${referenceCode}`
                    )
                    dispatch(setUserAlias(response.data.data.userAlias))
                    checkIfIdExistsInLocalStorageAndStore(response.data)
                    updateUI(response.data.data)
                } catch (error: any) {
                    dispatch(setError(error.message))
                } finally {
                    dispatch(setIsLoading(false))
                }
            }
            fetchData()
        } else {
            dispatch(setReferenceCode(''))
        }
    }, [dispatch])

    const updateUI = (data: any) => {
        dispatch(resetGrades())
        dispatch(setGrades(data.grades.grades))
        dispatch(setTwentyThreeFiveRule(data.grades.twentythreeFiveRule))
        dispatch(setBirthYear(data.experiences.birthYear))
        dispatch(setExperiences(data.experiences.experiences))
        dispatch(setEducationalPrograms(data.educationalPrograms.data))
        dispatch(setDefaultCourses(data.courses.defaultCourses))
        dispatch(setAdditionalCourses(data.courses.additionalCourses))
        dispatch(setFavoriteObjects(data.favoriteStudyFields.favoriteObjects))
    }

    const checkIfIdExistsInLocalStorageAndStore = (data: any) => {
        const lagredeKarakterer: string = 'lagredeKarakterer'
        const lsData: any = localStorage.getItem(lagredeKarakterer)
        let parsedLsData: any[] = Array.isArray(lsData) ? lsData : [lsData]

        if (lsData) {
            parsedLsData = JSON.parse(lsData)
        } else {
            parsedLsData = []
        }

        const idExists = parsedLsData.some(
            (grades: any) => grades.id === data.id
        )

        if (parsedLsData.length === 0 || !idExists) {
            parsedLsData.push(data)
            localStorage.setItem(
                lagredeKarakterer,
                JSON.stringify(parsedLsData)
            )
        } else {
            const updatedData = parsedLsData.map((grades: any) =>
                grades.id === data.id ? { ...grades, ...data } : grades
            )
            localStorage.setItem(lagredeKarakterer, JSON.stringify(updatedData))
        }
    }

    const handleScanFile = async (file: File) => {
     try {
        setScanError('')
        setScanInfo(null)
        setIsScanning(true)
        
        localStorage.removeItem('karakterkalkulator')
        localStorage.removeItem('lagredeKarakter')
        dispatch(resetGrades())

        const scan = await scanTranscriptImage(file)
        const legacyCourses = await getLegacyCourses()

        const {
            mapped,
            unknownCodes,
            matchedFromAdditional,
            matchedFromLegacy,
            unknownCourses,
        } = mapScanToCourseGrades(
            scan,
            defaultCourses,
            additionalCourses,
            legacyCourses
        )

        for (const c of matchedFromAdditional) {
            dispatch(addCourse(c))
        }
        for (const c of matchedFromLegacy) {
            dispatch(addCourse(c))
        }

        for (const c of unknownCourses) {
            dispatch(addCourse(c))
        }

        if (mapped.length > 0) {
            dispatch(setDefaultCourses(mapped.map(c => c.course)))
            dispatch(setGrades(mapped))
            setScanVersion(v => v + 1)
        }

        setScanInfo({
            usedPipeline: scan.used_pipeline ?? null,
            matchedCount: mapped.length,
            unknownCodes,
        })
    } catch (e: any) {
        setScanError(
            e?.response?.data?.detail ||
                e?.message ||
                'Noe gikk galt under skanning.'
        )
    } finally {
        setIsScanning(false)
    }
    }

    if (isLoading) {
        return (
            <AppLayout>
                <LoadingSpinner />
            </AppLayout>
        )
    }

    if (!isLoading && error) {
        return (
            <AppLayout>
                <Container className="animate-reveal">
                    <Text>
                        Dine lagrede karakterer under referansekode{' '}
                        {referenceCode} eksisterer ikke lengre i våre systemer.
                    </Text>
                    <Text>
                        Vi sletter referansekoder som ikke har vært aktive eller
                        benyttet på over 6 mnd.
                    </Text>
                </Container>
            </AppLayout>
        )
    }


    return (
        <AppLayout>
            <Container className="animate-reveal">
                <div className="mt-4 mb-2 flex items-center justify-between gap-3">
                    <Text type="title" popupContent={helperTextFillOutGrades}>
                        Fyll ut dine karakterer
                    </Text>

                    <div className="flex items-center gap-2">
                        <input
                            id="ai-scan-input"
                            type="file"
                            /*accept="image/png,image/jpeg"*/
                            accept=".pdf,image/png,image/jpeg,image/jpg,application/pdf"
                            className="hidden"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const f = e.target.files?.[0]
                                if (f) handleScanFile(f)
                                e.currentTarget.value = ''
                            }}
                        />
                        <SonansButton
                            variant="primary"
                            size="small"
                            disabled={isScanning}
                            className="karakterkalkulator_ai_skanner_btn"
                            onClick={() => {
                                const el = document.getElementById(
                                    'ai-scan-input'
                                ) as HTMLInputElement | null
                                el?.click()
                            }}
                        >
                            {isScanning ? 'Skanner…' : 'AI skanner'}
                        </SonansButton>
                    </div>
                </div>

                {!isScanning && !scanInfo && (
                    <div className="mb-3">
                        <HighlightBox background="yellow" className="karakterkalkulator_highlight_info">
                            <p>Du kan bruke AI-skanneren til å laste opp vitnemålet ditt. Da fylles fag og karakterer inn automatisk for deg.</p>
                            <p style={{ marginTop: '0.75rem' }}><strong>Merk:</strong> Skanningen kan være unøyaktig. Du kan enkelt rette opp feil manuelt etterpå.</p>
                        </HighlightBox>
                    </div>
                )}

                {isScanning && (
                    <div className="mb-3">
                        <Message
                            content={
                                <Text>
                                    Skanningen pågår. Dette kan ta noen sekunder.
                                </Text>
                            }
                        />
                    </div>
                )}

                {scanError && (
                    <div className="mb-3">
                        <Message content={<Text>{scanError}</Text>} />
                    </div>
                )}

                {scanInfo && !isScanning && (
                    <div className="mb-3">
                        <Message
                            color="yellow"
                            content={
                                <div className="flex flex-col gap-1">
                                    <Text>
                                        {scanInfo.matchedCount === 0
                                            ? 'Fant ingen fag. Du kan fylle inn karakterene manuelt.'
                                            : `Skanning fullført. Fant ${scanInfo.matchedCount} fag.`}
                                    </Text>

                                    {scanInfo && scanInfo.unknownCodes && scanInfo.unknownCodes.length > 0 && (
                                        <>
                                        <Text>
                                            Ukjente/Utgåtte fagkoder:{' '}
                                            {scanInfo.unknownCodes
                                                .slice(0, 6)
                                                .map((u) => u.kode)
                                                .join(', ')}
                                            {scanInfo.unknownCodes.length > 6 ? ' …' : ''}
                                        </Text>
                                        <Text>
                                            Alle fagkoder som hentes er etter nyeste reform, resten forsøkes å hentes inn.
                                        </Text>
                                        </>
                                    )}
                                </div>
                            }
                        />
                    </div>
                )}

                <div className="rounded-2xl p-4 sm:px-10 sm:pb-10 flex-1 mx-auto" style={{ backgroundColor: 'rgba(0, 8, 25, 0.04)' }}>
                    <div className="flex flex-col gap-8 max-w-sm md:max-w-5xl mx-auto">
                        <CourseList key={`${scanVersion}-${JSON.stringify(defaultCourses)}`} courses={defaultCourses} />
                        <div className="flex flex-col">
                            <Text className="mb-2 font-bold">Legg til fag</Text>
                            <CourseSearch />
                        </div>
                    </div>
                    <div className={'py-3'}>
                        <Message
                            color="yellow"
                            content={
                                <Text>
                                    Fellesfagene i utdanningsprogrammet ditt er
                                    lagt til automatisk. Husk å se over samt. å
                                    legge til dine valgfrie programfag.
                                </Text>
                            }
                        />
                    </div>
                </div>
                <Experiences />
            </Container>

            <div className=" py-4 mt-4">
                <Container>
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-8 md:p-10 mb-2">
                        <Results />
                        <div className={'py-3'}>
                            <Message
                                color="yellow"
                                content={
                                    <Text>
                                        Dette er en veiledende utregning, og
                                        kalkulatoren tar ikke høyde for om
                                        fagene du har lagt inn gir generell
                                        studiekompetanse.
                                    </Text>
                                }
                            />
                        </div>
                        <div className="flex flex-col items-center">
                            <Text className="font-bold mb-1">
                                Dine{' '}
                                {youngster && !betterThenRegular
                                    ? 'Karakterpoeng'
                                    : 'konkurransepoeng'}
                            </Text>

                            <ResultsCardScroll />
                        </div>
                    </div>
                    <div className="bg-white p-4 sm:p-8 md:p-10 rounded-xl border border-gray-200 shadow-sm">
                        <SaveResults />
                    </div>
                </Container>
            </div>

            <div className="">
                <Container className="flex justify-between">
                    <SonansButton
                        variant="link"
                        size="large"
                        withIcon
                        icon="arrow_back"
                        iconPosition="left"
                        type="button"
                        onClick={() => navigate('/start')}
                    >
                        Tilbake
                    </SonansButton>
                    {/* <SonansButton
                        variant="link"
                        size="large"
                        withIcon
                        type="button"
                        onClick={() => navigate('/studiemuligheter')}
                    >
                        Se dine studiemuligheter
                    </SonansButton> */}
                </Container>
            </div>
            <BookCounseling textType={'calc'} />
        </AppLayout>
    )
}